import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';
import swal from 'sweetalert2';

import { AuthService } from '../../auth/AuthService';
import { InsuranceService } from '../../Services/insurance.service';

interface AuditMilestone {
  label: string;
  eventType: string;
  complete: boolean;
  eventTime: string | null;
}

@Component({
  selector: 'pm-audit-finalization',
  templateUrl: './audit-finalization.component.html',
  styleUrls: ['./audit-finalization.component.css']
})
export class AuditFinalizationComponent implements OnInit {
  auditId = 0;
  loading = true;
  publishing = false;
  downloading = false;

  audit: any = {};
  timeline: any[] = [];
  revisions: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private insuranceService: InsuranceService,
    public authService: AuthService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.auditId = Number(this.route.snapshot.paramMap.get('id') || 0);

    if (!this.auditId) {
      this.router.navigate(['/audit/queue']);
      return;
    }

    this.loadPage();
  }

  loadPage(): void {
    const clientId = this.authService.getClientId();
    if (!clientId) {
      return;
    }

    this.loading = true;

    forkJoin([
      this.insuranceService.getAuditDetail(this.auditId, { clientId }).pipe(first()),
      this.insuranceService.getAuditTimeline(this.auditId, { clientId }).pipe(first()),
      this.insuranceService.getAuditRevisions(this.auditId, { clientId }).pipe(first())
    ]).subscribe(
      ([audit, timeline, revisions]: any[]) => {
        this.ngZone.run(() => {
          this.audit = audit || {};
          this.timeline = Array.isArray(timeline) ? timeline : [];
          this.revisions = Array.isArray(revisions) ? revisions : [];
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
      (error: any) => {
        this.ngZone.run(() => {
          this.loading = false;
          this.cdr.detectChanges();
        });

        this.showError('Unable to Load Audit Finalization', error);
      }
    );
  }

  get hasPerfectScore(): boolean {
    // Repository publication requires an exact 100% score.
    return Number(this.audit?.score) === 100;
  }

  get canPublish(): boolean {
    return this.audit?.status === 'PASSED' &&
      this.hasPerfectScore &&
      this.authService.hasRole('audit-edit');
  }

  get isPublished(): boolean {
    return this.audit?.status === 'PUBLISHED';
  }

  get milestones(): AuditMilestone[] {
    const eventTime = (types: string[]): string | null => {
      const match = this.timeline.find(item => types.includes(item?.event_type));
      return match?.event_time || null;
    };

    const published = eventTime(['PUBLISHED']);
    const passed = eventTime(['PASSED']);
    const started = eventTime(['STARTED', 'IN_AUDIT']);
    const submitted = eventTime(['SUBMITTED']);

    return [
      { label: 'Submitted', eventType: 'SUBMITTED', complete: !!submitted, eventTime: submitted },
      { label: 'In Audit', eventType: 'STARTED', complete: !!started || !!passed || !!published, eventTime: started },
      { label: 'Passed', eventType: 'PASSED', complete: !!passed || !!published, eventTime: passed },
      { label: 'Published', eventType: 'PUBLISHED', complete: !!published || this.isPublished, eventTime: published }
    ];
  }

  get currentRevision(): any {
    if (!this.revisions.length) {
      return null;
    }

    const revisionId = Number(this.audit?.revision_id);
    return this.revisions.find(item => Number(item?.id) === revisionId) ||
      this.revisions[this.revisions.length - 1];
  }

  get patientName(): string {
    return this.audit?.patient_name || this.registrationValue('patientName') ||
      [
        this.registrationValue('firstName'),
        this.registrationValue('lastName')
      ].filter(Boolean).join(' ') ||
      'Patient';
  }

  get patientFirstName(): string {
    return this.registrationValue('firstName') || this.patientName.split(' ')[0] || '-';
  }

  get patientLastName(): string {
    return this.registrationValue('lastName') || this.patientName.split(' ').slice(1).join(' ') || '-';
  }

  get patientDob(): string {
    return this.registrationValue('dob') || this.registrationValue('patientDob') || '-';
  }

  get carrierName(): string {
    return this.registrationValue('insuranceProviderName') ||
      this.registrationValue('payerName') ||
      this.audit?.snapshot?.PERSONAL?.payer?.name ||
      '-';
  }

  get officeName(): string {
    return this.registrationValue('officeName') ||
      this.registrationValue('office') ||
      '-';
  }

  get planType(): string {
    return this.registrationValue('planType') || '-';
  }

  get networkType(): string {
    return this.registrationValue('networkType') || '-';
  }

  get groupId(): string {
    return this.registrationValue('groupId') ||
      this.audit?.snapshot?.PERSONAL?.insurance?.group_no ||
      '-';
  }

  get memberId(): string {
    return this.registrationValue('memberId') ||
      this.audit?.snapshot?.PERSONAL?.patient?.member_id ||
      '-';
  }

  get insurancePhone(): string {
    return this.registrationValue('insurancePhoneNumber') || '-';
  }

  get auditedBy(): string {
    const auditorId = this.audit?.audited_by;
    const actors = this.audit?.actors || {};

    return this.audit?.audited_by_name ||
      this.audit?.auditor_name ||
      (auditorId ? (actors[auditorId] || auditorId) : '-') ||
      '-';
  }

  async publishToRepository(): Promise<void> {
    if (!this.canPublish || this.publishing) {
      return;
    }

    const confirmed = await swal.fire({
      icon: 'question',
      title: 'Publish to Repository?',
      text: 'This will publish the reviewed group benefits to the EVM repository.',
      showCancelButton: true,
      confirmButtonText: 'Publish',
      cancelButtonText: 'Cancel'
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    this.publishing = true;

    try {
      const response = await this.insuranceService.publishAudit(
        this.auditId,
        { version: Number(this.audit?.version ?? 0) },
        { clientId: this.authService.getClientId() }
      ).pipe(first()).toPromise();

      this.audit = { ...this.audit, ...response };
      this.publishing = false;

      await swal.fire({
        icon: 'success',
        title: 'Published to Repository',
        text: 'The audited group benefits are now available in the repository.'
      });

      this.loadPage();
    } catch (error) {
      this.publishing = false;
      this.showError('Unable to Publish Audit', error);
    }
  }

  downloadForm(format: 'excel' | 'pdf'): void {
    const patientId = Number(this.audit?.patient_id);
    if (!patientId || this.downloading) {
      return;
    }

    this.downloading = true;

    const params = {
      patientId,
      format,
      clientId: this.authService.getClientId()
    };

    this.insuranceService.getExportFile(null, params).pipe(first()).subscribe(
      (response: any) => {
        try {
          if (!response?.body) {
            throw new Error('Empty file received');
          }

          const contentDisposition = response.headers?.get('Content-Disposition') || '';
          const match = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
          const extension = format === 'pdf' ? 'pdf' : 'xlsx';
          const fallbackName = `patient-${patientId}.${extension}`;
          const fileName = match ? decodeURIComponent(match[1] || match[2]) : fallbackName;
          const contentType =
            response.headers?.get('Content-Type') ||
            (format === 'pdf'
              ? 'application/pdf'
              : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

          const blob = new Blob([response.body], { type: contentType });
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(downloadUrl);

          this.downloading = false;

          swal.fire({
            icon: 'success',
            title: 'Download Successful',
            text: `The file "${fileName}" has been downloaded successfully.`,
            showConfirmButton: false,
            timer: 2200
          });
        } catch (error) {
          this.downloading = false;
          swal.fire({
            icon: 'error',
            title: 'Download Failed',
            text: 'An unexpected error occurred while processing the file.'
          });
        }
      },
      (error: any) => {
        this.downloading = false;
        this.showError('Download Failed', error);
      }
    );
  }

  goBack(): void {
    this.router.navigate(['/audit/queue']);
  }

  formatStatus(value: string): string {
    return (value || '')
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private registrationValue(key: string): any {
    const registration = this.audit?.snapshot?.PERSONAL?.registration || {};
    return registration?.[key];
  }

  private showError(title: string, error: any): void {
    swal.fire({
      icon: error?.status === 409 ? 'warning' : 'error',
      title,
      text:
        error?.error?.message ||
        error?.error?.detail ||
        error?.message ||
        'Something went wrong. Please try again.'
    });
  }
}
